import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { RequestStatus } from 'src/app/request-v2/models/request-status.model';
import { RequestStatusService } from 'src/app/request-v2/services/request-status.service';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { finalize } from 'rxjs/operators';
import { SelectV2Component } from 'src/app/global/components/forms/select-v2/select.component';

@Component({
    selector: 'app-status-migration-dialog',
    templateUrl: './status-migration-dialog.component.html',
    styleUrls: ['./status-migration-dialog.component.scss'],
})
export class StatusMigrationDialogComponent implements OnInit {
    @ViewChild('statusSelect') statusSelectComponent: SelectV2Component;

    availableStatuses: any[] = [];

    selectedStatus: any = null;

    selectedStatusId: string | null = null;

    currentLang = 'en';

    isLoading = false;

    isSaving = false;

    constructor(
        public dialogRef: MatDialogRef<StatusMigrationDialogComponent>,
        private translate: TranslateService,
        private requestStatusService: RequestStatusService,
        private messageService: MessageService,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            statusId: string;
            statusName: string;
        },
    ) {}

    ngOnInit(): void {
        this.currentLang = this.translate.currentLang;
        this.fetchStatuses();
    }

    fetchStatuses(): void {
        this.isLoading = true;
        this.requestStatusService
            .getDefaultStatusList()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe(
                (statuses: RequestStatus[]) => {
                    this.availableStatuses = statuses
                        .filter((status) => status._id !== this.data.statusId)
                        .map((status) => ({
                            value: status._id,
                            label: status.name as string,
                        }));
                },
                (_error) => {
                    this.messageService.error(this.translate.instant('request_v2.status_migration.fetch_error'));
                },
            );
    }

    onSelectChange(selectedItem: any): void {
        this.selectedStatus = selectedItem;

        let statusId = null;
        if (selectedItem) {
            statusId = selectedItem.value || selectedItem.id || selectedItem._id;
        }

        if (statusId) {
            this.selectedStatusId = String(statusId);
        } else {
            this.selectedStatusId = null;
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    save(): void {
        if (!this.selectedStatusId && this.statusSelectComponent && this.statusSelectComponent.value) {
            const directValue = this.statusSelectComponent.value;
            if (directValue && directValue.value) {
                this.selectedStatusId = String(directValue.value);
            }
        }

        if (!this.selectedStatusId) {
            return;
        }

        this.isSaving = true;
        this.requestStatusService
            .migrateStatus(this.data.statusId, this.selectedStatusId)
            .pipe(
                finalize(() => {
                    this.isSaving = false;
                }),
            )
            .subscribe(
                (result) => {
                    const successMessage = this.translate.instant('request_v2.status_migration.success', { count: result.migratedCount });
                    this.messageService.success(successMessage);

                    // After successful migration, delete the original status
                    this.deleteOriginalStatus();
                },
                (_error) => {
                    const errorMessage = this.translate.instant('request_v2.status_migration.error');
                    this.messageService.error(errorMessage);
                },
            );
    }

    private deleteOriginalStatus(): void {
        this.requestStatusService.delete(this.data.statusId).subscribe(
            () => {
                const deleteSuccessMessage = this.translate.instant('request_v2.status_list.delete_success');
                this.messageService.success(deleteSuccessMessage);
                this.dialogRef.close({ migrated: true, deleted: true });
            },
            (_error) => {
                const deleteErrorMessage = this.translate.instant('request_v2.status_list.delete_error');
                this.messageService.error(deleteErrorMessage);
                this.dialogRef.close({ migrated: true, deleted: false });
            },
        );
    }
}
